<template>
  <div class="reading-devices-page">
    <!-- Background section -->
    <div class="bg-section"></div>

    <!-- Add Reading Device Overlay -->
    <AddReadingDeviceOverlay
        ref="addDeviceOverlay"
        :is-visible="showAddDeviceOverlay"
        @close="closeAddDeviceOverlay"
        @submit="handleAddDevice"
    />

    <AppHeader title="Reading Devices" />
    <div class="content">
      <!-- Header section with title and action buttons -->
      <div class="page-header">
        <div class="header-left">
          <h2 class="section-title">READING DEVICES</h2>
          <p class="section-subtitle">Manage gateway devices for meter readings</p>
        </div>
        <div class="header-right">
          <div class="action-buttons">
            <button class="action-button" @click="openAddDeviceOverlay">
              <span class="plus-icon">+</span> Add Device
            </button>
            <button
                class="action-button"
                @click="refreshDevices"
                :disabled="isLoading"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              Refresh
            </button>
          </div>
        </div>
      </div>

      <!-- Reading Devices Table -->
      <div class="table-wrapper">
        <div class="devices-table">
          <!-- Table Header -->
          <div class="table-header">
            <div class="table-cell">IDENTIFICATION NUMBER</div>
            <div class="table-cell">TYPE</div>
            <div class="table-cell">CREATED AT</div>
            <div class="table-cell">ACTIONS</div>
          </div>

          <!-- Loading State -->
          <div v-if="isLoading" class="loading-state">
            <div class="loading-spinner"></div>
            <span>Loading reading devices...</span>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="error-state">
            <div class="error-message">{{ error }}</div>
            <button class="retry-button" @click="fetchDevices">Retry</button>
          </div>

          <!-- Empty State -->
          <div v-else-if="devices.length === 0" class="empty-state">
            <div class="empty-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                <rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect>
                <rect x="9" y="9" width="6" height="6"></rect>
                <line x1="9" y1="1" x2="9" y2="4"></line>
                <line x1="15" y1="1" x2="15" y2="4"></line>
                <line x1="9" y1="20" x2="9" y2="23"></line>
                <line x1="15" y1="20" x2="15" y2="23"></line>
                <line x1="20" y1="9" x2="23" y2="9"></line>
                <line x1="20" y1="14" x2="23" y2="14"></line>
                <line x1="1" y1="9" x2="4" y2="9"></line>
                <line x1="1" y1="14" x2="4" y2="14"></line>
              </svg>
            </div>
            <p>No reading devices found</p>
            <button class="action-button" @click="openAddDeviceOverlay">
              Add your first device
            </button>
          </div>

          <!-- Table Rows -->
          <div v-else>
            <div
                v-for="device in paginatedDevices"
                :key="device.identification_number"
                class="table-row"
                @click="selectDevice(device)"
                :class="{ 'selected': selectedDevice && selectedDevice.identification_number === device.identification_number }"
            >
              <div class="table-cell">
                <span class="device-id">{{ device.identification_number }}</span>
              </div>
              <div class="table-cell">
                <span class="device-type">{{ device.type || 'N/A' }}</span>
              </div>
              <div class="table-cell">
                {{ formatDateTime(device.created_at) }}
              </div>
              <div class="table-cell">
                <div class="action-buttons">
                  <button
                      class="action-button-small delete-button"
                      @click.stop="deleteDevice(device)"
                      title="Delete Device"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="devices.length > pageSize" class="pagination-controls">
          <div class="pagination-info">
            Showing {{ startItem }}-{{ endItem }} of {{ devices.length }} items
          </div>
          <div class="pagination-buttons">
            <button
                class="pagination-button"
                :disabled="currentPage === 1"
                @click="changePage(currentPage - 1)"
            >
              Previous
            </button>

            <div class="page-numbers">
              <button
                  v-for="page in visiblePageNumbers"
                  :key="page"
                  :class="['page-number', { active: currentPage === page }]"
                  @click="changePage(page)"
              >
                {{ page }}
              </button>
            </div>

            <button
                class="pagination-button"
                :disabled="currentPage === totalPages"
                @click="changePage(currentPage + 1)"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppHeader from './AppHeader.vue';
import AddReadingDeviceOverlay from './AddReadingDeviceOverlay.vue';

export default {
  name: 'ReadingDevicesPage',
  components: {
    AppHeader,
    AddReadingDeviceOverlay
  },
  data() {
    return {
      devices: [],
      selectedDevice: null,
      isLoading: false,
      error: '',
      showAddDeviceOverlay: false,

      // Pagination
      currentPage: 1,
      pageSize: 10
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.devices.length / this.pageSize));
    },
    startItem() {
      return this.devices.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    },
    endItem() {
      return Math.min(this.startItem + this.paginatedDevices.length - 1, this.devices.length);
    },
    paginatedDevices() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.devices.slice(start, end);
    },
    visiblePageNumbers() {
      const maxVisible = 5;
      const half = Math.floor(maxVisible / 2);

      let start = Math.max(1, this.currentPage - half);
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end === this.totalPages) {
        start = Math.max(1, end - maxVisible + 1);
      }

      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  mounted() {
    this.fetchDevices();
  },
  methods: {
    async fetchDevices() {
      this.isLoading = true;
      this.error = '';

      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL;
        const response = await fetch(`${apiBaseUrl}/readingdevices`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Error fetching devices: ${response.statusText}`);
        }

        const data = await response.json();
        this.devices = Array.isArray(data) ? data : [];

      } catch (error) {
        console.error('Error fetching reading devices:', error);
        this.error = 'Failed to load reading devices. Please try again.';
        this.devices = [];
      } finally {
        this.isLoading = false;
      }
    },

    async deleteDevice(device) {
      if (!confirm(`Are you sure you want to delete device "${device.identification_number}"?`)) {
        return;
      }

      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL;
        const encodedId = encodeURIComponent(device.identification_number);
        const deleteUrl = `${apiBaseUrl}/readingdevices/${encodedId}`;

        console.log('Deleting device at URL:', deleteUrl);

        const response = await fetch(deleteUrl, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });

        let responseData;
        try {
          responseData = await response.json();
        } catch (e) {
          responseData = { message: 'Failed to delete device' };
        }

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Device not found');
          } else if (response.status === 403) {
            throw new Error('You do not have permission to delete this device');
          } else if (response.status === 400) {
            throw new Error(responseData.message || 'Invalid request');
          } else {
            throw new Error(responseData.message || `Failed to delete device (${response.status})`);
          }
        }

        // Success - remove from local list
        this.devices = this.devices.filter(d => d.identification_number !== device.identification_number);

        if (this.selectedDevice && this.selectedDevice.identification_number === device.identification_number) {
          this.selectedDevice = null;
        }

        if (this.paginatedDevices.length === 0 && this.currentPage > 1) {
          this.currentPage--;
        }

        alert(`Device "${device.identification_number}" deleted successfully.`);

      } catch (error) {
        console.error('Error deleting device:', error);
        alert(error.message || 'Failed to delete device. Please try again.');
      }
    },

    selectDevice(device) {
      this.selectedDevice = device;
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid Date';

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'Error';
      }
    },

    openAddDeviceOverlay() {
      this.showAddDeviceOverlay = true;
    },

    closeAddDeviceOverlay() {
      this.showAddDeviceOverlay = false;
    },

    async handleAddDevice(deviceData) {
      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL;

        // Prepare the request body - safely handle potentially undefined values
        const requestBody = {
          identification_number: (deviceData.identification_number || '').trim(),
          type: deviceData.type || ''
        };

        // Only add tenant_id if it exists and is not empty
        if (deviceData.tenant_id) {
          const trimmedTenantId = (deviceData.tenant_id || '').trim();
          if (trimmedTenantId) {
            requestBody.tenant_id = trimmedTenantId;
          }
        }

        console.log('Sending request body:', requestBody); // Debug log

        const response = await fetch(`${apiBaseUrl}/readingdevices`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        // Try to parse response as JSON
        let responseData;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          responseData = await response.json();
        } else {
          const text = await response.text();
          console.error('Non-JSON response:', text);
          throw new Error('Server returned non-JSON response');
        }

        console.log('Response status:', response.status); // Debug log
        console.log('Response data:', responseData); // Debug log

        if (!response.ok) {
          // Handle specific error cases
          let errorMessage = responseData.message || responseData.detail || responseData.error || 'Failed to create device';

          if (response.status === 409 || errorMessage.toLowerCase().includes('already exists')) {
            errorMessage = `A device with identification number "${deviceData.identification_number}" already exists.`;
          } else if (response.status === 400 && errorMessage.toLowerCase().includes('identification number')) {
            errorMessage = 'Identification number is required or invalid.';
          }

          throw new Error(errorMessage);
        }

        // Success - notify the overlay component
        if (this.$refs.addDeviceOverlay) {
          this.$refs.addDeviceOverlay.onSubmitSuccess();
        }

        // Refresh the device list
        await this.fetchDevices();

        // Show success message
        alert(`Device "${requestBody.identification_number}" created successfully!`);

        // Close the overlay
        this.closeAddDeviceOverlay();

      } catch (error) {
        console.error('Error creating device:', error);

        // Notify the overlay component of the error
        if (this.$refs.addDeviceOverlay) {
          this.$refs.addDeviceOverlay.onSubmitError(error.message);
        }
      }
    },

    refreshDevices() {
      this.fetchDevices();
    }
  }
};
</script>

<style scoped>
.reading-devices-page {
  min-height: 100vh;
  color: white;
  font-family: 'Inter', sans-serif;
  position: relative;
}

/* Background section */
.bg-section {
  position: absolute;
  top: 0;
  left: 33%;
  width: 67%;
  height: 100%;
  background-color: rgba(86, 86, 86, 0.1);
  z-index: 0;
}

.content {
  padding: 100px 110px;
  position: relative;
  z-index: 1;
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
}

.header-left {
  flex: 1;
}

.section-title {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.15em;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.section-subtitle {
  font-size: 14px;
  opacity: 0.7;
}

.header-right {
  display: flex;
  align-items: center;
}

/* Action buttons */
.action-buttons {
  display: flex;
  gap: 16px;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.plus-icon {
  font-size: 16px;
  font-weight: bold;
}

/* Table wrapper */
.table-wrapper {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  padding: 24px;
}

/* Devices table */
.devices-table {
  width: 100%;
}

.table-header {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 12px 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.table-row {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  padding: 16px 0;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.table-row:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.table-row.selected {
  background-color: rgba(255, 255, 255, 0.1);
}

.table-cell {
  flex: 1;
  padding: 0 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-cell:first-child {
  flex: 1.5;
}

.device-id {
  font-weight: 600;
  color: #4fc3f7;
}

.device-type {
  text-transform: uppercase;
  font-size: 11px;
  opacity: 0.9;
}

.tenant-id {
  font-size: 11px;
  opacity: 0.7;
}

/* Action buttons in table */
.action-buttons {
  display: flex;
  gap: 8px;
}

.action-button-small {
  background: none;
  border: none;
  color: white;
  padding: 4px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
  border-radius: 4px;
}

.action-button-small:hover {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.1);
}

.delete-button:hover {
  color: #ff8a80;
}

/* Loading state */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 16px;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error state */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 15px;
}

.error-message {
  color: #ff8a80;
  text-align: center;
}

.retry-button {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  text-align: center;
}

.empty-icon {
  opacity: 0.2;
  margin-bottom: 24px;
}

.empty-state p {
  font-size: 16px;
  margin-bottom: 24px;
  opacity: 0.8;
}

/* Pagination */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.pagination-info {
  font-size: 12px;
  opacity: 0.7;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pagination-button {
  background: none;
  border: none;
  color: white;
  font-size: 12px;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.2s;
}

.pagination-button:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
  opacity: 1;
}

.pagination-button:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

.page-numbers {
  display: flex;
  gap: 6px;
}

.page-number {
  background: none;
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.page-number:hover:not(.active) {
  background-color: rgba(255, 255, 255, 0.1);
}

.page-number.active {
  background-color: rgba(255, 255, 255, 0.2);
  font-weight: 600;
}

/* Responsive */
@media (max-width: 1200px) {
  .content {
    padding: 100px 60px;
  }
}

@media (max-width: 768px) {
  .content {
    padding: 80px 20px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }

  .table-cell:nth-child(3),
  .table-cell:nth-child(4) {
    display: none;
  }

  .pagination-controls {
    flex-direction: column;
    gap: 16px;
  }
}
</style>